import InteractionEvent, {
  type InteractionTargetType
} from './InteractionEvent';

export type NavigationLinkPlacement =
  | 'breadcrumb'
  | 'meganav'
  | 'module'
  | 'phone_menu'
  | 'product_information'
  | 'related_categories';

export type NavigationLinkModuleType =
  | 'homepage-popular-brands'
  | 'homepage-sub-brands'
  | 'homepage-top-categories';

export type NavigationLinkTargetProperties = {
  ancestor_labels?: string[];
  destination_path: string;
  label?: string;
  module_position?: number;
  module_type?: NavigationLinkModuleType;
  placement: NavigationLinkPlacement;
  position: number;
};

type EventData = {
  ancestorLabels?: string[];
  destinationPath: string;
  gesture?: string;
  guestId?: string;
  isBot: boolean;
  label?: string;
  modulePosition?: number;
  moduleType?: NavigationLinkModuleType;
  placement: NavigationLinkPlacement;
  position: number;
};

class NavigationLinkInteractionEvent extends InteractionEvent<
  EventData,
  NavigationLinkTargetProperties
> {
  protected get targetType(): InteractionTargetType {
    return 'navigation_link';
  }

  // Every control this fires from navigates away, and most of them are plain anchors that
  // unload the document as the beacon leaves.
  protected get keepalive(): boolean {
    return true;
  }

  // `label` and `ancestor_labels` are the header menus' identity: the menu is authored in this
  // repo, so there is no dimension to join a link's text or its section from. `module_type` and
  // `module_position` are the homepage modules' rank space, the pair every module-borne click
  // carries. Each is sent only where its placement owes it; an absent key never becomes null.
  protected getTargetProperties(): NavigationLinkTargetProperties {
    return {
      ancestor_labels: this.data.ancestorLabels,
      destination_path: this.destinationPath(),
      label: this.data.label,
      module_position: this.data.modulePosition,
      module_type: this.data.moduleType,
      placement: this.data.placement,
      position: this.data.position
    };
  }

  // The href as rendered minus the origin: pathname plus any query string, so a womens
  // department crumb keeps its ?gender=womens and the value joins to the destination view's
  // page_path. The controls render paths, so this only ever strips an origin that one of them
  // happened to render absolute (the phone menu's profile link does).
  private destinationPath(): string {
    const url = new URL(
      this.data.destinationPath,
      'https://placeholder.invalid'
    );

    return `${url.pathname}${url.search}`;
  }
}

export default NavigationLinkInteractionEvent;
